import {useState} from 'react'
import {
  ArrowUpDown,
  ChevronRight,
  ChevronDown,
  MessageSquare,
  Paperclip,
  ListChecks,
  CheckSquare,
  Square,
  Minus,
} from 'lucide-react'
import type {Task, Priority} from '../../types'
import {useApp} from '../../context/AppContext'
import {getPriorityConfig, getStatusConfig, formatDate} from '../../utils/helpers'
import Avatar from '../common/Avatar'
import Badge from '../common/Badge'
import {LabelBadge} from '../common/Badge'

export type SortField =
  | 'title'
  | 'priority'
  | 'status'
  | 'dueDate'
  | 'storyPoints'
  | 'createdAt'
  | 'board'
export type SortDirection = 'asc' | 'desc'

interface TasksTableProps {
  tasks: Task[]
  sortField: SortField
  sortDirection: SortDirection
  onSort: (field: SortField) => void
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onToggleSelectAll: () => void
}

const priorityOrder: Record<Priority, number> = {critical: 0, high: 1, medium: 2, low: 3}

export default function TasksTable({
  tasks,
  sortField,
  sortDirection,
  onSort,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: TasksTableProps) {
  const {getUserById, getLabelById, getBoardById, dispatch} = useApp()

  const allSelected = tasks.length > 0 && selectedIds.size === tasks.length
  const someSelected = selectedIds.size > 0 && !allSelected

  const SelectAllIcon = allSelected ? CheckSquare : someSelected ? Minus : Square

  return (
    <div className='devops-table-shell'>
      <div className='overflow-x-auto'>
        <table className='w-full min-w-[900px]'>
          <thead>
            <tr className='border-b border-slate-100 bg-slate-50/50'>
              <th className='w-12 py-3 px-3'>
                <button
                  onClick={onToggleSelectAll}
                  className='text-slate-400 hover:text-brand-500 transition-colors'
                  aria-label='Selecionar todas'
                >
                  <SelectAllIcon size={18} />
                </button>
              </th>
              <th className='text-left py-3 px-4'>
                <SortHeader
                  field='title'
                  current={sortField}
                  direction={sortDirection}
                  onSort={onSort}
                >
                  Tarefa
                </SortHeader>
              </th>
              <th className='text-left py-3 px-4'>
                <SortHeader
                  field='board'
                  current={sortField}
                  direction={sortDirection}
                  onSort={onSort}
                >
                  Quadro
                </SortHeader>
              </th>
              <th className='text-left py-3 px-4'>
                <SortHeader
                  field='status'
                  current={sortField}
                  direction={sortDirection}
                  onSort={onSort}
                >
                  Status
                </SortHeader>
              </th>
              <th className='text-left py-3 px-4'>
                <SortHeader
                  field='priority'
                  current={sortField}
                  direction={sortDirection}
                  onSort={onSort}
                >
                  Prioridade
                </SortHeader>
              </th>
              <th className='text-left py-3 px-4'>
                <span className='text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Responsável
                </span>
              </th>
              <th className='text-left py-3 px-4'>
                <span className='text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Etiquetas
                </span>
              </th>
              <th className='text-center py-3 px-4'>
                <SortHeader
                  field='storyPoints'
                  current={sortField}
                  direction={sortDirection}
                  onSort={onSort}
                >
                  Pontos
                </SortHeader>
              </th>
              <th className='text-left py-3 px-4'>
                <SortHeader
                  field='dueDate'
                  current={sortField}
                  direction={sortDirection}
                  onSort={onSort}
                >
                  Prazo
                </SortHeader>
              </th>
              <th className='text-center py-3 px-4'>
                <span className='text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Info
                </span>
              </th>
              <th className='w-10' />
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                selected={selectedIds.has(task.id)}
                onToggleSelect={onToggleSelect}
                getUserById={getUserById}
                getLabelById={getLabelById}
                getBoardById={getBoardById}
                dispatch={dispatch}
              />
            ))}
            {tasks.length === 0 && (
              <tr>
                <td colSpan={11} className='text-center py-20 text-sm text-slate-400'>
                  <div className='flex flex-col items-center gap-2'>
                    <ListChecks size={40} className='text-slate-200' />
                    <p>Nenhuma tarefa encontrada</p>
                    <p className='text-xs text-slate-300'>Tente ajustar os filtros de busca</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function SortHeader({
  field,
  current,
  direction,
  onSort,
  children,
}: {
  field: SortField
  current: SortField
  direction: SortDirection
  onSort: (field: SortField) => void
  children: React.ReactNode
}) {
  const active = field === current
  return (
    <button
      onClick={() => onSort(field)}
      className='flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700 transition-colors'
    >
      {children}
      <ArrowUpDown
        size={12}
        className={`transition-colors ${active ? 'text-brand-500' : 'text-slate-300'}`}
      />
      {active && (
        <ChevronDown
          size={10}
          className={`text-brand-500 transition-transform ${
            direction === 'asc' ? 'rotate-180' : ''
          }`}
        />
      )}
    </button>
  )
}

function TaskRow({
  task,
  selected,
  onToggleSelect,
  getUserById,
  getLabelById,
  getBoardById,
  dispatch,
}: {
  task: Task
  selected: boolean
  onToggleSelect: (id: string) => void
  getUserById: (id: string) => ReturnType<ReturnType<typeof useApp>['getUserById']>
  getLabelById: (id: string) => ReturnType<ReturnType<typeof useApp>['getLabelById']>
  getBoardById: (id: string) => ReturnType<ReturnType<typeof useApp>['getBoardById']>
  dispatch: ReturnType<typeof useApp>['dispatch']
}) {
  const [hovered, setHovered] = useState(false)
  const priority = getPriorityConfig(task.priority)
  const status = getStatusConfig(task.status)
  const board = getBoardById(task.boardId)
  const assignees = task.assigneeIds.map((id) => getUserById(id)).filter(Boolean)
  const taskLabels = task.labelIds.map((id) => getLabelById(id)).filter(Boolean)

  const isOverdue = task.dueDate && task.status !== 'done' && new Date(task.dueDate) < new Date()
  const subtasksDone = task.subtasks.filter((s) => s.completed).length
  const subtasksTotal = task.subtasks.length

  const CheckIcon = selected ? CheckSquare : Square

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`border-b border-slate-50 cursor-pointer transition-colors ${
        selected ? 'bg-brand-50/40' : hovered ? 'bg-slate-50' : ''
      }`}
    >
      <td className='py-3 px-3' onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onToggleSelect(task.id)}
          className={`transition-colors ${
            selected ? 'text-brand-500' : 'text-slate-300 hover:text-slate-500'
          }`}
          aria-label={`Selecionar ${task.title}`}
        >
          <CheckIcon size={18} />
        </button>
      </td>
      <td
        className='py-3 px-4 max-w-[280px]'
        onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}
      >
        <span className='text-sm font-medium text-slate-700 hover:text-brand-600 transition-colors line-clamp-1'>
          {task.title}
        </span>
      </td>
      <td className='py-3 px-4' onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}>
        {board && (
          <span className='text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md'>
            {board.name}
          </span>
        )}
      </td>
      <td className='py-3 px-4' onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}>
        <Badge className={status.color}>{status.label}</Badge>
      </td>
      <td className='py-3 px-4' onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}>
        <Badge className={priority.color} dot={priority.dot}>
          {priority.label}
        </Badge>
      </td>
      <td className='py-3 px-4' onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}>
        <div className='flex items-center -space-x-1.5'>
          {assignees.slice(0, 3).map((user) => (
            <Avatar key={user!.id} name={user!.name} color={user!.avatarColor} size='sm' />
          ))}
          {assignees.length > 3 && (
            <span className='text-xs text-slate-400 ml-2'>+{assignees.length - 3}</span>
          )}
        </div>
      </td>
      <td className='py-3 px-4' onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}>
        <div className='flex flex-wrap gap-1'>
          {taskLabels.slice(0, 2).map((label) => (
            <LabelBadge key={label!.id} name={label!.name} color={label!.color} />
          ))}
          {taskLabels.length > 2 && (
            <span className='text-xs text-slate-400'>+{taskLabels.length - 2}</span>
          )}
        </div>
      </td>
      <td
        className='py-3 px-4 text-center'
        onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}
      >
        {task.storyPoints > 0 && (
          <span className='inline-flex items-center justify-center w-7 h-7 rounded-lg bg-slate-100 text-xs font-bold text-slate-600'>
            {task.storyPoints}
          </span>
        )}
      </td>
      <td className='py-3 px-4' onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}>
        {task.dueDate && (
          <span
            className={`text-sm ${isOverdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}
          >
            {formatDate(task.dueDate)}
          </span>
        )}
      </td>
      <td className='py-3 px-4' onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}>
        <div className='flex items-center justify-center gap-2.5 text-slate-400'>
          {task.comments.length > 0 && (
            <span
              className='inline-flex items-center gap-0.5 text-xs'
              title={`${task.comments.length} comentários`}
            >
              <MessageSquare size={13} />
              {task.comments.length}
            </span>
          )}
          {task.attachments.length > 0 && (
            <span
              className='inline-flex items-center gap-0.5 text-xs'
              title={`${task.attachments.length} anexos`}
            >
              <Paperclip size={13} />
              {task.attachments.length}
            </span>
          )}
          {subtasksTotal > 0 && (
            <span
              className={`inline-flex items-center gap-0.5 text-xs ${
                subtasksDone === subtasksTotal ? 'text-emerald-500' : ''
              }`}
              title={`${subtasksDone}/${subtasksTotal} subtarefas`}
            >
              <ListChecks size={13} />
              {subtasksDone}/{subtasksTotal}
            </span>
          )}
        </div>
      </td>
      <td className='py-3 px-4' onClick={() => dispatch({type: 'SELECT_TASK', payload: task.id})}>
        <ChevronRight
          size={16}
          className={`transition-colors ${hovered ? 'text-brand-400' : 'text-slate-300'}`}
        />
      </td>
    </tr>
  )
}
